import { z } from "zod";

/**
 * E-Mail-Domains, über die sich Berater:innen einer Sparkasse selbst anmelden
 * können.
 *
 * Die Plattformrolle hinterlegt je Mandant eine Liste von Mustern. Wer sich mit
 * einer passenden Adresse einen Anmeldelink schicken lässt, bekommt beim
 * Einlösen automatisch ein Beraterkonto in genau dieser Sparkasse. Ohne Muster
 * bleibt es beim bisherigen Verhalten: nur eingeladene Adressen kommen herein.
 *
 * Zwei Formen sind erlaubt:
 *   `sparkasse-luebeck.de`   exakt diese Domain
 *   `*.sparkasse.de`         jede Subdomain, aber nicht die Domain selbst
 */

/** Zeichenvorrat einer Domain-Marke, absichtlich ohne Unicode/Punycode. */
const LABEL = "[a-z0-9]([a-z0-9-]*[a-z0-9])?";
const DOMAIN_PATTERN = new RegExp(`^(\\*\\.)?(${LABEL}\\.)+[a-z]{2,}$`);

/**
 * Freimail-Anbieter. Eine solche Domain als Muster würde die halbe Welt zu
 * Berater:innen einer Sparkasse machen, deshalb wird sie abgewiesen.
 */
export const PUBLIC_EMAIL_DOMAINS = [
  "aol.com",
  "freenet.de",
  "gmail.com",
  "googlemail.com",
  "gmx.at",
  "gmx.ch",
  "gmx.de",
  "gmx.net",
  "hotmail.com",
  "hotmail.de",
  "icloud.com",
  "live.com",
  "live.de",
  "mail.com",
  "me.com",
  "outlook.com",
  "outlook.de",
  "posteo.de",
  "proton.me",
  "protonmail.com",
  "t-online.de",
  "web.de",
  "yahoo.com",
  "yahoo.de",
  "zoho.com",
] as const;

const PUBLIC_SET = new Set<string>(PUBLIC_EMAIL_DOMAINS);

/**
 * Bringt eine Eingabe auf die Speicherform: Kleinschreibung, ohne `@`, ohne
 * Protokoll, ohne abschließenden Punkt. Was jemand aus einer Mailadresse
 * kopiert, soll ohne Nacharbeit funktionieren.
 */
export function normalizeEmailDomainPattern(raw: string): string {
  let value = raw.trim().toLowerCase();
  value = value.replace(/^https?:\/\//, "");
  value = value.replace(/^@/, "");
  // Wer eine ganze Adresse einfügt, meint deren Domain.
  const at = value.lastIndexOf("@");
  if (at >= 0) value = value.slice(at + 1);
  value = value.replace(/\/.*$/, "");
  value = value.replace(/\.+$/, "");
  return value;
}

/** Die Domain einer Mailadresse, klein geschrieben. Leer, wenn keine da ist. */
export function emailDomainOf(email: string): string {
  const at = email.lastIndexOf("@");
  if (at < 0) return "";
  return email.slice(at + 1).trim().toLowerCase().replace(/\.+$/, "");
}

export function isWildcardDomainPattern(pattern: string): boolean {
  return pattern.startsWith("*.");
}

/**
 * Passt die Domain auf das Muster? Das Wildcard-Muster deckt ausschließlich
 * Subdomains ab: `*.sparkasse.de` trifft `haspa.sparkasse.de`, nicht
 * `sparkasse.de`. Wer beides will, hinterlegt beides.
 */
export function matchesEmailDomainPattern(
  pattern: string,
  domain: string,
): boolean {
  const p = pattern.trim().toLowerCase();
  const d = domain.trim().toLowerCase();
  if (!p || !d) return false;
  if (isWildcardDomainPattern(p)) return d.endsWith(p.slice(1)) && d !== p.slice(2);
  return d === p;
}

/**
 * Wie eng ein Muster gefasst ist. Trifft eine Adresse auf mehrere Muster, gilt
 * das engste: die exakte Domain schlägt die Wildcard, die längere Wildcard die
 * kürzere.
 */
export function emailDomainSpecificity(pattern: string): number {
  const labels = pattern.split(".").filter(Boolean).length;
  return isWildcardDomainPattern(pattern) ? labels : labels + 100;
}

/**
 * Prüft ein Muster fachlich. Gibt die Begründung zurück, nicht nur ja/nein,
 * damit die Oberfläche sagen kann, was falsch ist.
 */
export function validateEmailDomainPattern(pattern: string): string | null {
  if (!pattern) return "Domain darf nicht leer sein.";
  if (pattern.length > 253) return "Domain ist zu lang.";
  if (!DOMAIN_PATTERN.test(pattern)) {
    return `„${pattern}" ist keine gültige Domain. Erlaubt sind sparkasse-beispiel.de oder *.sparkasse-beispiel.de.`;
  }
  const base = isWildcardDomainPattern(pattern) ? pattern.slice(2) : pattern;
  if (PUBLIC_SET.has(base)) {
    return `„${base}" ist ein öffentlicher Mailanbieter und kann keiner Sparkasse zugeordnet werden.`;
  }
  // `*.de` wäre schon an der Regex gescheitert, `*.co.uk` nicht.
  if (isWildcardDomainPattern(pattern) && base.split(".").length < 2) {
    return `„${pattern}" ist zu weit gefasst.`;
  }
  return null;
}

/** Zod-Schema für ein einzelnes Muster, inklusive Normalisierung. */
export const emailDomainPatternSchema = z
  .string()
  .transform(normalizeEmailDomainPattern)
  .superRefine((value, ctx) => {
    const problem = validateEmailDomainPattern(value);
    if (problem) ctx.addIssue({ code: "custom", message: problem });
  });

/**
 * Der Request-Body zum Setzen der Domains eines Mandanten. Bewusst die ganze
 * Liste statt einzelner Zugänge: die Oberfläche zeigt die Liste, der Server
 * bekommt genau das, was dort steht.
 */
export const updateEmailDomainsSchema = z.object({
  domains: z.array(emailDomainPatternSchema).max(25),
});
export type UpdateEmailDomainsInput = z.infer<typeof updateEmailDomainsSchema>;

/**
 * Überschneiden sich zwei Muster, decken sie also mindestens eine Adresse
 * gemeinsam ab? Zwei Sparkassen dürfen sich keine Domain teilen, sonst wäre bei
 * der Selbstanmeldung nicht entscheidbar, wohin ein Konto gehört.
 */
export function emailDomainPatternsCollide(a: string, b: string): boolean {
  if (a === b) return true;
  const aWild = isWildcardDomainPattern(a);
  const bWild = isWildcardDomainPattern(b);
  if (aWild && bWild) {
    return (
      matchesEmailDomainPattern(a, b.slice(2)) ||
      matchesEmailDomainPattern(b, a.slice(2))
    );
  }
  if (aWild) return matchesEmailDomainPattern(a, b);
  if (bWild) return matchesEmailDomainPattern(b, a);
  return false;
}

/**
 * Sucht unter den Mandanten den, der eine Adresse aufnehmen darf.
 *
 * Treffen mehrere Mandanten gleich eng zu, bleibt das Ergebnis leer: eine
 * Adresse automatisch der falschen Sparkasse zuzuschlagen wäre schlimmer, als
 * gar nicht anzulegen.
 */
export function resolveEmailDomainOwner<T extends { allowedEmailDomains: string[] }>(
  email: string,
  candidates: T[],
): T | null {
  const domain = emailDomainOf(email);
  if (!domain) return null;

  let best: { tenant: T; specificity: number } | null = null;
  let ambiguous = false;

  for (const candidate of candidates) {
    for (const pattern of candidate.allowedEmailDomains) {
      if (!matchesEmailDomainPattern(pattern, domain)) continue;
      const specificity = emailDomainSpecificity(pattern);
      if (!best || specificity > best.specificity) {
        best = { tenant: candidate, specificity };
        ambiguous = false;
      } else if (specificity === best.specificity && best.tenant !== candidate) {
        ambiguous = true;
      }
    }
  }

  if (!best || ambiguous) return null;
  return best.tenant;
}

/**
 * Ein Anzeigename aus der Mailadresse, solange niemand einen echten gepflegt
 * hat. `anna.meier@…` wird zu „Anna Meier".
 */
export function displayNameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1));
  return words.join(" ") || email;
}
